import { useAuth } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
	ApiClient,
	type CreateRuleRequest,
	UnauthorizedError,
	type UpdateRuleRequest,
} from "../lib/api";

export function useRules() {
	const { getToken, signOut } = useAuth();
	const client = new ApiClient(getToken);

	const query = useQuery({
		queryKey: ["rules"],
		queryFn: () => client.getRules(),
		retry: (failureCount, error) => {
			if (error instanceof UnauthorizedError) return false;
			return failureCount < 3;
		},
	});

	useEffect(() => {
		if (query.error instanceof UnauthorizedError) {
			void signOut();
		}
	}, [query.error, signOut]);

	return query;
}

export function useCreateRule() {
	const { getToken } = useAuth();
	const client = new ApiClient(getToken);
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (rule: CreateRuleRequest) => client.createRule(rule),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["rules"] });
		},
	});
}

export function useUpdateRule() {
	const { getToken } = useAuth();
	const client = new ApiClient(getToken);
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (rule: UpdateRuleRequest) => client.updateRule(rule),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["rules"] });
		},
	});
}

export function useDeleteRule() {
	const { getToken } = useAuth();
	const client = new ApiClient(getToken);
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => client.deleteRule(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["rules"] });
		},
	});
}

export function useSetRuleEnabled() {
	const { getToken } = useAuth();
	const client = new ApiClient(getToken);
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ enabled, id }: { enabled: boolean; id: string }) =>
			client.setRuleEnabled(id, enabled),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["rules"] });
		},
	});
}

export function useParseRule() {
	const { getToken } = useAuth();
	const client = new ApiClient(getToken);

	return useMutation({
		mutationFn: (prompt: string) => client.parseRule(prompt),
	});
}
